import { c as _c } from "react/compiler-runtime";
import { useToast } from '@common/hooks';
import { Base64 } from 'js-base64';
import { useEffect, useRef } from 'react';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { ensureRenderableImage } from '@/utils/heif';
import { useCheckWelloApp } from './useCheckWelloApp';
interface UseFileUploadProps {
  accept?: string;
  multiple?: boolean;
}
export const useFileUpload = t0 => {
  const $ = _c(19);
  const {
    accept,
    multiple: t1
  } = t0;
  const multiple = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      type: MESSAGE_KEYWORD.IMAGE
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    request,
    response
  } = useHandleApp(t2);
  const {
    isWelloApp
  } = useCheckWelloApp();
  const {
    toast
  } = useToast();
  const image64 = response?.data?.image;
  const resolveRef = useRef(null);
  const resolveFilesRef = useRef(null);
  const file = useRef(undefined);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const files = useRef(t3);
  let t4;
  let t5;
  if ($[2] !== image64 || $[3] !== toast) {
    t4 = () => {
      if (!image64) {
        return;
      }
      let isCancelled = false;
      const buildAndResolve = async () => {
        const byteCharacters = Base64.atob(image64);
        const byteArrays = [];
        for (let offset = 0; offset < byteCharacters.length; offset = offset + 512, offset) {
          const slice = byteCharacters.slice(offset, offset + 512);
          const byteNumbers = new Array(slice.length);
          for (let index = 0; index < slice.length; index++) {
            byteNumbers[index] = slice.codePointAt(index) ?? 0;
          }
          byteArrays.push(new Uint8Array(byteNumbers));
        }
        const blob = new Blob(byteArrays, {
          type: "image/png"
        });
        let resolvedFile;
        try {
          resolvedFile = await ensureRenderableImage(new File([blob], "image.png", {
            type: "image/png"
          }));
        } catch {
          if (!isCancelled) {
            toast({
              message: "\uC774\uBBF8\uC9C0 \uBCC0\uD658\uC5D0 \uC2E4\uD328\uD588\uC5B4\uC694. \uB2E4\uB978 \uC0AC\uC9C4\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694.",
              type: "fail"
            });
          }
        }
        if (isCancelled) {
          return;
        }
        file.current = resolvedFile;
        resolveRef.current?.(resolvedFile);
        resolveRef.current = null;
        files.current = resolvedFile ? [resolvedFile] : [];
        resolveFilesRef.current?.(files.current);
        resolveFilesRef.current = null;
      };
      buildAndResolve();
      return () => {
        isCancelled = true;
      };
    };
    t5 = [image64, toast];
    $[2] = image64;
    $[3] = toast;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== accept || $[7] !== isWelloApp || $[8] !== request || $[9] !== toast) {
    t6 = pickMultiple => {
      resolveRef.current?.(undefined);
      resolveRef.current = null;
      resolveFilesRef.current?.([]);
      resolveFilesRef.current = null;
      file.current = undefined;
      files.current = [];
      if (isWelloApp) {
        request();
      } else {
        const fileInput = document.createElement("input");
        fileInput.type = "file";
        if (accept) {
          fileInput.accept = accept;
        }
        if (pickMultiple) {
          fileInput.multiple = true;
        }
        fileInput.style.appearance = "hide";
        fileInput.addEventListener("change", async event => {
          const input = event.target as HTMLInputElement;
          if (input.files && input.files.length > 0) {
            const selectedFiles = Array.from(input.files);
            const validFiles = [];
            for (const selectedFile of selectedFiles) {
              try {
                validFiles.push(await ensureRenderableImage(selectedFile));
              } catch {
                continue;
              }
            }
            if (validFiles.length < selectedFiles.length) {
              toast({
                message: "\uC77C\uBD80 \uC774\uBBF8\uC9C0\uB97C \uBCC0\uD658\uD558\uC9C0 \uBABB\uD588\uC5B4\uC694.",
                type: "fail"
              });
            }
            file.current = validFiles[0];
            files.current = validFiles;
          }
          resolveRef.current?.(file.current);
          resolveRef.current = null;
          resolveFilesRef.current?.(files.current);
          resolveFilesRef.current = null;
        });
        fileInput.addEventListener("cancel", () => {
          resolveRef.current?.(undefined);
          resolveRef.current = null;
          resolveFilesRef.current?.([]);
          resolveFilesRef.current = null;
        });
        fileInput.click();
      }
    };
    $[6] = accept;
    $[7] = isWelloApp;
    $[8] = request;
    $[9] = toast;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const openFilePicker = t6;
  let t7;
  if ($[11] !== openFilePicker) {
    t7 = async () => {
      openFilePicker(false);
      return new Promise(resolve => {
        resolveRef.current = resolve;
      });
    };
    $[11] = openFilePicker;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== multiple || $[14] !== openFilePicker) {
    t8 = async () => {
      openFilePicker(multiple);
      return new Promise(resolve_0 => {
        resolveFilesRef.current = resolve_0;
      });
    };
    $[13] = multiple;
    $[14] = openFilePicker;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7 || $[17] !== t8) {
    t9 = {
      getFile: t7,
      getFiles: t8
    };
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
};